/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import { services } from '../../services';
import PartnershipAssessmentsPanel from './PartnershipAssessmentsPanel';

interface Partner {
  _id: string;
  partnershipTypes: string[];
  deletedAt: string | null;
  organizationName: string;
  primaryContactName: string;
  firstName: string;
  lastName: string;
  socialProfile: string;
  websiteUrl: string;
  emailOrTelegram: string;
  country: string;
  otherPartnershipType: string;
  comments: string;
  submittedAt: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface PartnersPageProps {}

const PartnersPage: React.FC<PartnersPageProps> = () => {
  // Partners and partnership assessments share this tab
  const [view, setView] = useState<'partners' | 'assessments'>('partners');
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPartners, setTotalPartners] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [partnerLoading, setPartnerLoading] = useState(false);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching partners from:', API_ENDPOINTS.PARTNERS.LIST);
      const response = await services.get(API_ENDPOINTS.PARTNERS.LIST);

      console.log('Partners API Response:', response);

      if (response.data && response.data.success) {
        const partnersData = response.data.data || [];
        setPartners(Array.isArray(partnersData) ? partnersData : []);
        setTotalPartners(response.data.total || partnersData.length);
        setTotalPages(response.data.pages || 1);
        setCurrentPage(response.data.page || 1);
      } else {
        console.log('No partners data found, using fallback');
        setPartners([]);
        setTotalPartners(0);
        setTotalPages(1);
        setCurrentPage(1);
      }
    } catch (err: any) {
      console.error('Error fetching partners:', err);
      setError(err.response?.data?.message || 'Failed to fetch partners');
      setPartners([]);
      setTotalPartners(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartnerDetails = async (partnerId: string) => {
    try {
      setPartnerLoading(true);
      console.log('Fetching partner details for ID:', partnerId);
      const response = await services.get(API_ENDPOINTS.PARTNERS.GET_BY_ID(partnerId));

      console.log('Partner Details API Response:', response);

      if (response.data && response.data.success) {
        setSelectedPartner(response.data.data);
        setShowPartnerModal(true);
      } else {
        console.error('Failed to fetch partner details');
      }
    } catch (err: any) {
      console.error('Error fetching partner details:', err);
    } finally {
      setPartnerLoading(false);
    }
  };

  // Filter partners based on search term
  const filteredPartners = partners.filter((partner) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      partner.organizationName?.toLowerCase().includes(searchLower) ||
      partner.primaryContactName?.toLowerCase().includes(searchLower) ||
      partner.firstName?.toLowerCase().includes(searchLower) ||
      partner.lastName?.toLowerCase().includes(searchLower) ||
      partner.emailOrTelegram?.toLowerCase().includes(searchLower) ||
      partner.country?.toLowerCase().includes(searchLower) ||
      partner.partnershipTypes?.some((type) => type.toLowerCase().includes(searchLower))
    );
  });

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getPartnershipTypesBadges = (types: string[]) => {
    return types.map((type, index) => (
      <span
        key={index}
        className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mr-1 mb-1'
        style={{
          backgroundColor: '#465705',
          color: '#ede8c5',
        }}
      >
        {type}
      </span>
    ));
  };

  const renderViewSwitch = () => (
    <div className='flex gap-2 mb-8'>
      {(
        [
          ['partners', 'Partners'],
          ['assessments', 'Partnership Assessments'],
        ] as const
      ).map(([key, label]) => (
        <button
          key={key}
          onClick={() => setView(key)}
          className='px-5 py-2 rounded-lg font-work-sans text-sm transition-all duration-200'
          style={{
            backgroundColor: view === key ? '#bbe425' : '#0B222C',
            color: view === key ? '#0B222C' : '#ede8c5',
            border: '1px solid #095D52',
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );

  // Rendered before the partners fetch states so a partners error can't trap the tab
  if (view === 'assessments') {
    return (
      <div className='px-8 py-8' style={{ backgroundColor: '#0B222C', minHeight: '100vh' }}>
        {renderViewSwitch()}
        <PartnershipAssessmentsPanel />
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className='flex items-center justify-center min-h-screen'
        style={{ backgroundColor: '#0B222C' }}
      >
        <div className='text-center'>
          <div
            className='animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4'
            style={{ borderColor: '#095D52' }}
          ></div>
          <p className='font-work-sans' style={{ color: '#ede8c5' }}>
            Loading partners...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className='flex items-center justify-center min-h-screen'
        style={{ backgroundColor: '#0B222C' }}
      >
        <div className='text-center'>
          <div className='text-red-500 text-xl mb-4'>⚠️</div>
          <p className='font-work-sans text-lg mb-4' style={{ color: '#ede8c5' }}>
            {error}
          </p>
          <button
            onClick={fetchPartners}
            className='px-6 py-2 rounded-lg font-work-sans transition-all duration-300 hover:scale-105'
            style={{
              backgroundColor: '#095D52',
              border: '1px solid #095D52',
              color: '#ede8c5',
              boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#bbe425';
              e.currentTarget.style.boxShadow =
                '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#095D52';
              e.currentTarget.style.boxShadow =
                '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='px-8 py-8' style={{ backgroundColor: '#0B222C', minHeight: '100vh' }}>
      {renderViewSwitch()}
      {/* Header */}
      <div className='mb-10'>
        <h1
          className='text-3xl font-bold mb-2 font-dm-serif'
          style={{
            color: '#ede8c5',
            textShadow: '0 2px 6px rgba(0, 0, 0, 0.4), 0 0 10px rgba(187, 228, 37, 0.2)',
            letterSpacing: '0.5px',
          }}
        >
          Partner Management
        </h1>
        <p className='text-lg font-work-sans' style={{ color: '#ede8c5' }}>
          Manage and view all partnership submissions
        </p>
      </div>

      {/* Statistics Cards */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-8 mb-10'>
        <div
          className='rounded-lg p-8 border shadow-lg transition-all duration-300 hover:scale-105'
          style={{
            backgroundColor: '#0B222C',
            borderColor: '#095D52',
            boxShadow:
              '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
            backgroundImage:
              'linear-gradient(135deg, rgba(187, 228, 37, 0.05) 0%, rgba(9, 93, 82, 0.1) 50%, rgba(11, 34, 44, 0.95) 100%)',
          }}
        >
          <div className='flex items-center'>
            <div className='p-3 rounded-lg mr-4 transition-all duration-300 hover:scale-110'>
              <svg
                className='w-6 h-6'
                style={{ color: '#095D52' }}
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path d='M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z' />
              </svg>
            </div>
            <div>
              <p className='text-sm font-medium font-work-sans' style={{ color: '#ede8c5' }}>
                Total Partners
              </p>
              <p className='text-2xl font-bold font-dm-serif' style={{ color: '#ede8c5' }}>
                {totalPartners}
              </p>
            </div>
          </div>
        </div>

        <div
          className='rounded-lg p-8 border shadow-lg transition-all duration-300 hover:scale-105'
          style={{
            backgroundColor: '#0B222C',
            borderColor: '#095D52',
            boxShadow:
              '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
            backgroundImage:
              'linear-gradient(135deg, rgba(187, 228, 37, 0.05) 0%, rgba(9, 93, 82, 0.1) 50%, rgba(11, 34, 44, 0.95) 100%)',
          }}
        >
          <div className='flex items-center'>
            <div className='p-3 rounded-lg mr-4 transition-all duration-300 hover:scale-110'>
              <svg
                className='w-6 h-6'
                style={{ color: '#095D52' }}
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <div>
              <p className='text-sm font-medium font-work-sans' style={{ color: '#ede8c5' }}>
                Strategic Partners
              </p>
              <p className='text-2xl font-bold font-dm-serif' style={{ color: '#ede8c5' }}>
                {
                  partners.filter((partner) =>
                    partner.partnershipTypes?.includes('Strategic Partner')
                  ).length
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className='mb-8'>
        <div className='relative'>
          <input
            type='text'
            placeholder='Search partners by organization, contact name, email, country, or partnership type...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className='w-full px-4 py-3 pl-10 rounded-lg text-sm font-work-sans'
            style={{
              backgroundColor: '#0B222C',
              border: '1px solid #095D52',
              color: '#ede8c5',
              outline: 'none',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
            }}
          />
          <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
            <svg
              className='h-5 w-5'
              style={{ color: '#ede8c5' }}
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Partners Table */}
      <div className='mb-10'>
        <div className='flex items-center justify-between mb-6'>
          <h2
            className='text-2xl font-bold font-dm-serif'
            style={{
              color: '#ede8c5',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 15px rgba(187, 228, 37, 0.3)',
              letterSpacing: '0.8px',
            }}
          >
            Partners Directory
          </h2>
          <div className='flex items-center space-x-4'>
            <div className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
              {filteredPartners.length} of {totalPartners} partners
            </div>
            <div className='w-2 h-2 rounded-full' style={{ backgroundColor: '#4ade80' }}></div>
          </div>
        </div>

        <div
          className='overflow-x-auto rounded-lg shadow-lg transition-all duration-300 max-w-full'
          style={{
            backgroundColor: '#0B222C',
            border: '1px solid #095D52',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2)',
            backgroundImage:
              'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
          }}
        >
          <table className='w-full'>
            <thead
              style={{
                backgroundColor: '#095D52',
                backgroundImage:
                  'linear-gradient(135deg, rgba(187, 228, 37, 0.15) 0%, rgba(9, 93, 82, 0.9) 100%)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(187, 228, 37, 0.2)',
              }}
            >
              <tr>
                <th
                  className='px-4 py-3 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Organization
                </th>
                <th
                  className='px-4 py-3 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Contact
                </th>
                <th
                  className='px-4 py-3 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Partnership Types
                </th>
                <th
                  className='px-4 py-3 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Location
                </th>
                <th
                  className='px-4 py-3 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Website
                </th>
                <th
                  className='px-4 py-3 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Submitted
                </th>
                <th
                  className='px-4 py-3 text-center text-xs font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody
              style={{
                backgroundColor: '#0B222C',
                backgroundImage:
                  'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
              }}
              className='divide-y divide-gray-700'
            >
              {!Array.isArray(filteredPartners) || filteredPartners.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className='px-6 py-12 text-center font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    <div className='flex flex-col items-center'>
                      <div
                        className='w-16 h-16 rounded-full flex items-center justify-center mb-4'
                        style={{ backgroundColor: '#095D52' }}
                      >
                        <svg
                          className='w-8 h-8'
                          style={{ color: '#ede8c5' }}
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
                          />
                        </svg>
                      </div>
                      <p className='text-lg font-medium mb-2'>
                        {searchTerm
                          ? `No partners found matching "${searchTerm}"`
                          : 'No partners found'}
                      </p>
                      <p className='text-sm opacity-75'>
                        {searchTerm
                          ? 'Try adjusting your search criteria'
                          : 'Partners will appear here once submitted'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredPartners.map((partner: Partner, index: number) => (
                  <tr
                    key={partner._id || index}
                    className='transition-all duration-200 hover:scale-[1.01]'
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#095D52';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(9, 93, 82, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <td className='px-4 py-3'>
                      <div className='flex items-center'>
                        <div
                          className='w-8 h-8 rounded-lg flex items-center justify-center mr-2'
                          style={{ backgroundColor: '#095D52' }}
                        >
                          <svg
                            className='w-4 h-4'
                            style={{ color: '#ede8c5' }}
                            fill='currentColor'
                            viewBox='0 0 20 20'
                          >
                            <path
                              fillRule='evenodd'
                              d='M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z'
                              clipRule='evenodd'
                            />
                          </svg>
                        </div>
                        <div>
                          <div
                            className='text-xs font-semibold font-work-sans'
                            style={{ color: '#ede8c5' }}
                          >
                            {partner.organizationName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className='px-4 py-3'>
                      <div>
                        <div
                          className='text-xs font-medium font-work-sans'
                          style={{ color: '#ede8c5' }}
                        >
                          {partner.primaryContactName}
                        </div>
                        <div
                          className='text-xs font-work-sans opacity-75'
                          style={{ color: '#ede8c5' }}
                        >
                          {partner.emailOrTelegram}
                        </div>
                      </div>
                    </td>
                    <td className='px-4 py-3'>
                      <div className='flex flex-wrap gap-1'>
                        {partner.partnershipTypes && partner.partnershipTypes.length > 0 ? (
                          <>
                            <div className='flex flex-wrap gap-1'>
                              {partner.partnershipTypes.slice(0, 1).map((type, typeIndex) => (
                                <span
                                  key={typeIndex}
                                  className='inline-flex items-center px-2 py-1 rounded text-xs font-medium font-work-sans'
                                  style={{
                                    backgroundColor: '#095D52',
                                    color: '#ede8c5',
                                    border: '1px solid rgba(187, 228, 37, 0.2)',
                                  }}
                                >
                                  {type}
                                </span>
                              ))}
                              {partner.partnershipTypes.length > 1 && (
                                <span
                                  className='inline-flex items-center px-2 py-1 rounded text-xs font-medium font-work-sans'
                                  style={{
                                    backgroundColor: 'rgba(187, 228, 37, 0.15)',
                                    color: '#bbe425',
                                    border: '1px solid rgba(187, 228, 37, 0.4)',
                                  }}
                                >
                                  +{partner.partnershipTypes.length - 1}
                                </span>
                              )}
                            </div>
                          </>
                        ) : (
                          <span
                            className='text-xs font-work-sans opacity-60'
                            style={{ color: '#ede8c5' }}
                          >
                            None
                          </span>
                        )}
                      </div>
                    </td>
                    <td className='px-4 py-3'>
                      <div className='flex items-center'>
                        <svg
                          className='w-3 h-3 mr-1'
                          style={{ color: '#095D52' }}
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
                          />
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
                          />
                        </svg>
                        <span className='text-xs font-work-sans' style={{ color: '#ede8c5' }}>
                          {partner.country}
                        </span>
                      </div>
                    </td>
                    <td className='px-4 py-3'>
                      {partner.websiteUrl ? (
                        <a
                          href={partner.websiteUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='inline-flex items-center text-xs font-work-sans transition-colors duration-200'
                          style={{
                            color: '#60a5fa',
                            textDecoration: 'none',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#93c5fd';
                            e.currentTarget.style.textDecoration = 'underline';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#60a5fa';
                            e.currentTarget.style.textDecoration = 'none';
                          }}
                        >
                          <svg
                            className='w-3 h-3 mr-1'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                            />
                          </svg>
                          Visit
                        </a>
                      ) : (
                        <span
                          className='text-xs font-work-sans opacity-50'
                          style={{ color: '#ede8c5' }}
                        >
                          N/A
                        </span>
                      )}
                    </td>
                    <td className='px-4 py-3'>
                      <div className='text-xs font-work-sans' style={{ color: '#ede8c5' }}>
                        {formatDate(partner.submittedAt)}
                      </div>
                    </td>
                    <td className='px-4 py-3 text-center'>
                      <button
                        onClick={() => fetchPartnerDetails(partner._id)}
                        disabled={partnerLoading}
                        className='inline-flex items-center px-3 py-1.5 rounded text-xs font-medium font-work-sans transition-all duration-300 hover:scale-105'
                        style={{
                          backgroundColor: '#0B222C',
                          border: '1px solid #095D52',
                          color: '#ede8c5',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#095D52';
                          e.currentTarget.style.borderColor = '#bbe425';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(9, 93, 82, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#0B222C';
                          e.currentTarget.style.borderColor = '#095D52';
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                        }}
                      >
                        <svg
                          className='w-3 h-3 mr-1'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                          />
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                          />
                        </svg>
                        {partnerLoading ? 'Loading...' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          className='flex items-center justify-between pt-6'
          style={{ borderTop: '2px solid #095D52' }}
        >
          <div className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
            Page {currentPage} of {totalPages} • Showing {filteredPartners.length} of{' '}
            {totalPartners} partners
          </div>
          <div className='flex space-x-2'>
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className='px-4 py-2 rounded-lg text-sm font-medium font-work-sans transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed'
              style={{
                backgroundColor: currentPage === 1 ? '#095D5250' : '#095D52',
                color: '#ede8c5',
                border: '1px solid #095D52',
                boxShadow:
                  currentPage === 1
                    ? 'none'
                    : '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                backgroundImage:
                  currentPage === 1
                    ? 'none'
                    : 'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
              }}
              onMouseEnter={(e) => {
                if (currentPage !== 1) {
                  e.currentTarget.style.backgroundColor = '#bbe425';
                  e.currentTarget.style.boxShadow =
                    '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== 1) {
                  e.currentTarget.style.backgroundColor = '#095D52';
                  e.currentTarget.style.boxShadow =
                    '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                }
              }}
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className='px-4 py-2 rounded-lg text-sm font-medium font-work-sans transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed'
              style={{
                backgroundColor: currentPage === totalPages ? '#095D5250' : '#095D52',
                color: '#ede8c5',
                border: '1px solid #095D52',
                boxShadow:
                  currentPage === totalPages
                    ? 'none'
                    : '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                backgroundImage:
                  currentPage === totalPages
                    ? 'none'
                    : 'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
              }}
              onMouseEnter={(e) => {
                if (currentPage !== totalPages) {
                  e.currentTarget.style.backgroundColor = '#bbe425';
                  e.currentTarget.style.boxShadow =
                    '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== totalPages) {
                  e.currentTarget.style.backgroundColor = '#095D52';
                  e.currentTarget.style.boxShadow =
                    '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                }
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Partner Details Modal */}
      {showPartnerModal && selectedPartner && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div
            className='rounded-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-lg'
            style={{
              backgroundColor: '#0B222C',
              border: '2px solid #095D52',
              boxShadow:
                '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
            }}
          >
            <div
              className='flex items-center justify-between p-4'
              style={{ borderBottom: '2px solid #095D52' }}
            >
              <h3
                className='text-lg font-semibold font-dm-serif'
                style={{
                  color: '#ede8c5',
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 15px rgba(187, 228, 37, 0.3)',
                  letterSpacing: '0.8px',
                }}
              >
                Partner Details
              </h3>
              <button
                onClick={() => setShowPartnerModal(false)}
                className='text-2xl transition-all duration-300 hover:scale-110'
                style={{ color: '#ede8c5' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#bbe425';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#ede8c5';
                }}
              >
                <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>

            <div className='p-4'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                {/* Organization Information */}
                <div
                  className='rounded-lg p-3'
                  style={{
                    backgroundColor: '#0B222C',
                    border: '1px solid #095D52',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                  }}
                >
                  <h4
                    className='text-md font-semibold mb-4 font-dm-serif'
                    style={{ color: '#ede8c5' }}
                  >
                    Organization Information
                  </h4>
                  <div className='space-y-3'>
                    <div>
                      <label
                        className='text-sm font-medium font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Organization Name
                      </label>
                      <p className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                        {selectedPartner.organizationName}
                      </p>
                    </div>
                    <div>
                      <label
                        className='text-sm font-medium font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Primary Contact
                      </label>
                      <p className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                        {selectedPartner.primaryContactName}
                      </p>
                    </div>
                    <div>
                      <label
                        className='text-sm font-medium font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Contact Name
                      </label>
                      <p className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                        {selectedPartner.firstName} {selectedPartner.lastName}
                      </p>
                    </div>
                    <div>
                      <label
                        className='text-sm font-medium font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Email/Telegram
                      </label>
                      <p className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                        {selectedPartner.emailOrTelegram}
                      </p>
                    </div>
                    <div>
                      <label
                        className='text-sm font-medium font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Country
                      </label>
                      <p className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                        {selectedPartner.country}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Partnership Information */}
                <div
                  className='rounded-lg p-3'
                  style={{
                    backgroundColor: '#0B222C',
                    border: '1px solid #095D52',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                  }}
                >
                  <h4
                    className='text-md font-semibold mb-4 font-dm-serif'
                    style={{ color: '#ede8c5' }}
                  >
                    Partnership Information
                  </h4>
                  <div className='space-y-3'>
                    <div>
                      <label
                        className='text-sm font-medium font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Partnership Types
                      </label>
                      <div className='flex flex-wrap mt-1'>
                        {getPartnershipTypesBadges(selectedPartner.partnershipTypes || [])}
                      </div>
                    </div>
                    {selectedPartner.otherPartnershipType && (
                      <div>
                        <label
                          className='text-sm font-medium font-work-sans'
                          style={{ color: '#ede8c5' }}
                        >
                          Other Partnership Type
                        </label>
                        <p className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                          {selectedPartner.otherPartnershipType}
                        </p>
                      </div>
                    )}
                    <div>
                      <label
                        className='text-sm font-medium font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Website
                      </label>
                      <p className='text-sm'>
                        {selectedPartner.websiteUrl ? (
                          <a
                            href={selectedPartner.websiteUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-blue-400 hover:text-blue-300 underline'
                          >
                            {selectedPartner.websiteUrl}
                          </a>
                        ) : (
                          <span style={{ color: '#ede8c5' }}>N/A</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <label
                        className='text-sm font-medium font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Social Profile
                      </label>
                      <p className='text-sm'>
                        {selectedPartner.socialProfile ? (
                          <a
                            href={selectedPartner.socialProfile}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-blue-400 hover:text-blue-300 underline'
                          >
                            {selectedPartner.socialProfile}
                          </a>
                        ) : (
                          <span style={{ color: '#ede8c5' }}>N/A</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <label
                        className='text-sm font-medium font-work-sans'
                        style={{ color: '#ede8c5' }}
                      >
                        Submitted At
                      </label>
                      <p className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                        {formatDate(selectedPartner.submittedAt)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comments */}
              <div className='mt-6'>
                <h4
                  className='text-md font-semibold mb-4 font-dm-serif'
                  style={{ color: '#ede8c5' }}
                >
                  Comments
                </h4>
                <div
                  className='p-4 rounded-lg'
                  style={{
                    backgroundColor: '#0B222C',
                    border: '1px solid #095D52',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                  }}
                >
                  <p className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                    {selectedPartner.comments}
                  </p>
                </div>
              </div>

              {/* Modal Actions */}
              <div
                className='flex justify-end mt-6 pt-4'
                style={{ borderTop: '2px solid #095D52' }}
              >
                <button
                  onClick={() => setShowPartnerModal(false)}
                  className='px-4 py-2 rounded-lg text-sm font-medium font-work-sans transition-all duration-300 hover:scale-105'
                  style={{
                    backgroundColor: '#095D52',
                    color: '#ede8c5',
                    border: '1px solid #095D52',
                    boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                    backgroundImage:
                      'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                  }}
                  onMouseEnter={(e) => {
                    const target = e.target as HTMLButtonElement;
                    target.style.backgroundColor = '#bbe425';
                    target.style.boxShadow =
                      '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    const target = e.target as HTMLButtonElement;
                    target.style.backgroundColor = '#095D52';
                    target.style.boxShadow =
                      '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnersPage;
